import styled from '@emotion/styled';
import { Icon, Label } from '@britishcouncil/react-admin';
import {
  Grid,
  Header,
  Loader,
  Message,
  SemanticCOLORS,
  SemanticICONS,
  Segment,
} from 'semantic-ui-react';
import { useLrwSessionExamOverview } from './useLrwSessionExamOverview';
import { ExamOverviewGrid } from './ExamOverviewGrid';

type MetricItem = {
  label: string;
  icon: SemanticICONS;
  value: number;
  statusIcon?: SemanticICONS;
  statusColor?: SemanticCOLORS;
};

export const ExamOverview = () => {
  const { examOverview, isFetching, isNotFound } = useLrwSessionExamOverview();

  if (isFetching) {
    return (
      <LoadingWrapper>
        <Loader active inline="centered" />
      </LoadingWrapper>
    );
  }

  if (isNotFound) {
    return <Message negative content="Exam overview not found." />;
  }

  if (!examOverview) {
    return null;
  }

  const hasOtherLrwSeats = examOverview.otherLrwSessionRooms.length > 0;
  const hasMissingToCoverLrwWarning = examOverview.missingToCoverLrw > 0;
  const hasOtherLrwWarning = hasOtherLrwSeats;

  const lrwSeatMetrics: MetricItem[] = [
    {
      label: 'Planned',
      icon: 'users',
      value: examOverview.plannedLrwSeats,
    },
    {
      label: 'Booked',
      icon: 'users',
      value: examOverview.bookedLrwSeats,
    },
  ];

  const speakingSlotMetrics: MetricItem[] = [
    {
      label: 'Planned',
      icon: 'comment',
      value: examOverview.plannedSpeakingSlots,
    },
    {
      label: 'Booked',
      icon: 'comment',
      value: examOverview.bookedSpeakingSlots,
    },
    {
      label: 'Overlap LRW',
      icon: 'comment',
      value: examOverview.overlapLrw,
    },
    {
      label: 'Missing to cover LRW',
      icon: 'comment',
      value: examOverview.missingToCoverLrw,
      statusIcon: hasMissingToCoverLrwWarning ? 'warning sign' : 'check circle',
      statusColor: hasMissingToCoverLrwWarning ? 'yellow' : 'green',
    },
  ];

  const otherLrwMetrics: MetricItem[] = [
    {
      label: 'Planned',
      icon: 'users',
      value: examOverview.otherLrwPlannedSeats,
    },
  ];

  const renderMetric = (metric: MetricItem) => (
    <MetricBox key={metric.label}>
      <Header as="h5" size="tiny">
        {metric.label}
      </Header>

      <Label size="large">
        <Icon name={metric.icon} />
        {metric.value}
        {metric.statusIcon && (
          <StatusIconWrapper>
            <Icon name={metric.statusIcon} color={metric.statusColor} />
          </StatusIconWrapper>
        )}
      </Label>
    </MetricBox>
  );

  return (
    <Wrapper>
      <Segment>
        {(hasMissingToCoverLrwWarning || hasOtherLrwWarning) && (
          <WarningsWrapper>
            {hasMissingToCoverLrwWarning && (
              <Message warning icon>
                <Icon name="warning sign" />
                <Message.Content>
                  <Message.Header>Warning</Message.Header>
                  Insufficient Speaking slots number to cover all LRW planned seats. Unless they
                  are intended for L, R or W OSRs.
                </Message.Content>
              </Message>
            )}

            {hasOtherLrwWarning && (
              <Message warning icon>
                <Icon name="warning sign" />
                <Message.Content>
                  <Message.Header>Warning</Message.Header>
                  Speaking session serves seats for other LRW session(s).
                </Message.Content>
              </Message>
            )}
          </WarningsWrapper>
        )}

        <MetricsWrapper>
          <Grid stackable>
            <Grid.Row>
              <Grid.Column width={hasOtherLrwSeats ? 4 : 6}>
                <Header as="h3">LRW seats</Header>
                <MetricsRow>{lrwSeatMetrics.map(renderMetric)}</MetricsRow>
              </Grid.Column>

              <Grid.Column width={hasOtherLrwSeats ? 8 : 10}>
                <Header as="h3">Speaking slots</Header>
                <MetricsRow>{speakingSlotMetrics.map(renderMetric)}</MetricsRow>
              </Grid.Column>

              {hasOtherLrwSeats && (
                <Grid.Column width={4}>
                  <Header as="h3">Other LRW seats</Header>
                  <MetricsRow>{otherLrwMetrics.map(renderMetric)}</MetricsRow>
                </Grid.Column>
              )}
            </Grid.Row>
          </Grid>
          </MetricsWrapper>
      </Segment>

      <ExamOverviewGrid
        speakingSessionRooms={examOverview.speakingSessionRooms}
        lrwSessionRooms={examOverview.lrwSessionRooms}
        otherLrwSessionRooms={examOverview.otherLrwSessionRooms}
      />
    </Wrapper>
  );
};

const Wrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
  padding: 24px 32px 32px;
  overflow-x: hidden;
  background: #fff;

  .ui.segment {
    margin: 0;
  }

  .ui.grid > .row {
    padding-top: 0;
    padding-bottom: 0;
  }

  .ui.grid > .column > .ui.header {
    margin-bottom: 16px;
  }
`;

const WarningsWrapper = styled.div`
  margin-bottom: 24px;

  .ui.message {
    margin: 0 0 16px 0;
  }

  .ui.message:last-child {
    margin-bottom: 0;
  }
`;

const MetricsRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 12px 16px;
  align-items: flex-start;
`;

const MetricBox = styled.div`
  display: flex;
  flex-direction: column;

  .ui.header {
    margin-bottom: 8px;
  }

  .ui.label {
    white-space: nowrap;
  }
`;

const StatusIconWrapper = styled.span`
  display: inline-flex;
  margin-left: 8px;
`;

const LoadingWrapper = styled.div`
  padding: 32px;
`;

const MetricsWrapper = styled.div`
  margin-top: 32px;
  padding-bottom: 16px;
`;